/**
 * Toolbar Plugin for Lexical Editor
 * Provides formatting controls: Bold, Italic, Lists, Alignment, Links, Font Family, History
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND } from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isAtNodeEnd } from '@lexical/selection';
import { mergeRegister } from '@lexical/utils';
import {
  $getSelection,
  $isRangeSelection,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  type LexicalEditor,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND
} from 'lexical';
import type React from 'react';
import { Dropdown } from 'react-bootstrap';
import {
  ArrowClockwise,
  ArrowCounterclockwise,
  Link45deg,
  ListOl,
  ListUl,
  TextCenter,
  TextLeft,
  TextRight,
  TypeBold,
  TypeItalic,
  TypeUnderline
} from 'react-bootstrap-icons';

interface ToolbarPluginProps {
  editorRef: React.MutableRefObject<LexicalEditor | null>;
}

const LowPriority = 1;

export const ToolbarPlugin: React.FC<ToolbarPluginProps> = ({ editorRef }) => {
  const [editor] = useLexicalComposerContext();
  const toolbarRef = useRef<HTMLDivElement>(null);

  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [fontFamily, setFontFamily] = useState('Arial');

  // Store editor reference
  useEffect(() => {
    editorRef.current = editor;
  }, [editor, editorRef]);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      // Update text format states
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));

      // Update link state
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
      } else {
        setIsLink(false);
      }
    }
  }, []);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateToolbar();
          return false;
        },
        LowPriority
      ),
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        (payload) => {
          setCanUndo(payload);
          return false;
        },
        LowPriority
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        (payload) => {
          setCanRedo(payload);
          return false;
        },
        LowPriority
      )
    );
  }, [editor, updateToolbar]);

  const insertLink = useCallback(() => {
    if (!isLink) {
      const url = prompt('Enter URL:');
      if (url) {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
      }
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [editor, isLink]);

  const fontFamilyOptions = useMemo(
    () => [
      { label: 'Arial', value: 'Arial' },
      { label: 'Courier New', value: 'Courier New' },
      { label: 'Georgia', value: 'Georgia' },
      { label: 'Times New Roman', value: 'Times New Roman' },
      { label: 'Trebuchet MS', value: 'Trebuchet MS' },
      { label: 'Verdana', value: 'Verdana' }
    ],
    []
  );

  return (
    <div className="toolbar" ref={toolbarRef}>
      <div className="toolbar-group">
        <button
          type="button"
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
          }}
          className={`toolbar-item ${isBold ? 'active' : ''}`}
          aria-label="Format Bold"
          title="Bold"
        >
          <TypeBold />
        </button>
        <button
          type="button"
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
          }}
          className={`toolbar-item ${isItalic ? 'active' : ''}`}
          aria-label="Format Italic"
          title="Italic"
        >
          <TypeItalic />
        </button>
        <button
          type="button"
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
          }}
          className={`toolbar-item ${isUnderline ? 'active' : ''}`}
          aria-label="Format Underline"
          title="Underline"
        >
          <TypeUnderline />
        </button>
      </div>

      <div className="toolbar-divider" />

      <div className="toolbar-group">
        <button
          type="button"
          onClick={() => {
            editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
          }}
          className="toolbar-item"
          aria-label="Unordered List"
          title="Unordered List"
        >
          <ListUl />
        </button>
        <button
          type="button"
          onClick={() => {
            editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
          }}
          className="toolbar-item"
          aria-label="Ordered List"
          title="Ordered List"
        >
          <ListOl />
        </button>
      </div>

      <div className="toolbar-divider" />

      <div className="toolbar-group">
        <button
          type="button"
          onClick={() => {
            editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left');
          }}
          className="toolbar-item"
          aria-label="Align Left"
          title="Align Left"
        >
          <TextLeft />
        </button>
        <button
          type="button"
          onClick={() => {
            editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center');
          }}
          className="toolbar-item"
          aria-label="Align Center"
          title="Align Center"
        >
          <TextCenter />
        </button>
        <button
          type="button"
          onClick={() => {
            editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right');
          }}
          className="toolbar-item"
          aria-label="Align Right"
          title="Align Right"
        >
          <TextRight />
        </button>
      </div>

      <div className="toolbar-divider" />

      <div className="toolbar-group">
        <Dropdown className="toolbar-dropdown">
          <Dropdown.Toggle variant="light" size="sm" className="toolbar-item">
            {fontFamily}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {fontFamilyOptions.map((option) => (
              <Dropdown.Item
                key={option.value}
                onClick={() => {
                  setFontFamily(option.value);
                  editor.update(() => {
                    const selection = $getSelection();
                    if ($isRangeSelection(selection)) {
                      // Apply font family using style
                      selection.getNodes().forEach((node) => {
                        if ('setStyle' in node) {
                          (node as any).setStyle(`font-family: ${option.value}`);
                        }
                      });
                    }
                  });
                }}
              >
                {option.label}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <div className="toolbar-divider" />

      <div className="toolbar-group">
        <button
          type="button"
          onClick={insertLink}
          className={`toolbar-item ${isLink ? 'active' : ''}`}
          aria-label="Insert Link"
          title="Insert Link"
        >
          <Link45deg />
        </button>
      </div>

      <div className="toolbar-divider" />

      <div className="toolbar-group">
        <button
          type="button"
          disabled={!canUndo}
          onClick={() => {
            editor.dispatchCommand(UNDO_COMMAND, undefined);
          }}
          className="toolbar-item"
          aria-label="Undo"
          title="Undo"
        >
          <ArrowCounterclockwise />
        </button>
        <button
          type="button"
          disabled={!canRedo}
          onClick={() => {
            editor.dispatchCommand(REDO_COMMAND, undefined);
          }}
          className="toolbar-item"
          aria-label="Redo"
          title="Redo"
        >
          <ArrowClockwise />
        </button>
      </div>
    </div>
  );
};

function getSelectedNode(selection: any) {
  const anchor = selection.anchor;
  const focus = selection.focus;
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();
  if (anchorNode === focusNode) {
    return anchorNode;
  }
  const isBackward = selection.isBackward();
  if (isBackward) {
    return $isAtNodeEnd(focus) ? anchorNode : focusNode;
  } else {
    return $isAtNodeEnd(anchor) ? focusNode : anchorNode;
  }
}
